import { useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, FileSpreadsheet, FileText } from "lucide-react";
import { Button } from "./ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { DOC_TYPES } from "../lib/constants";
import { saveBlob } from "../lib/download";

const BASE = [
  ["name", "الاسم"], ["employee_number", "الرقم الوظيفي"], ["rider_id", "رقم المندوب"], ["branch", "الفرع"],
  ["department", "القسم"], ["job_title", "المسمى الوظيفي"], ["profession", "المهنة"], ["employee_status", "الحالة"],
  ["nationality", "الجنسية"], ["gender", "الجنس"], ["birth_date", "تاريخ الميلاد"], ["hire_date", "تاريخ التعيين"],
  ["phone", "رقم الجوال"], ["portal_email", "البريد الإلكتروني"], ["iqama_number", "رقم الهوية/الإقامة"],
  ["contract_type", "نوع العقد"], ["contract_start", "بداية العقد"], ["contract_end", "نهاية العقد"],
  ["email", "الإيميل"], ["national_address_short", "العنوان الوطني المختصر"], ["app_vehicle_type", "نوع المركبة في التطبيق"],
  ["passport_number", "رقم الجواز"], ["platform", "تطبيق المندوب"], ["vehicle_type", "نوع المركبة"],
  ["vehicle_number", "رقم المركبة"], ["fuel_code", "كود الوقود"], ["fuel_limit", "حد الوقود الشهري"],
  ["salary", "الراتب"], ["gosi.basic", "التأمينات - الأساسي"], ["gosi.housing", "التأمينات - السكن"],
  ["gosi.other", "التأمينات - بدلات أخرى"], ["gosi.total", "التأمينات - الإجمالي"],
  ["is_supervisor", "مشرف"], ["telegram_chat_id", "Chat ID تيليجرام"], ["created_at", "تاريخ الإضافة"],
];

const val = (e, k) => {
  const v = k.split(".").reduce((o, p) => (o == null ? o : o[p]), e);
  if (typeof v === "boolean") return v ? "نعم" : "لا";
  if (k === "created_at" && v) return String(v).slice(0, 10);
  return v ?? "";
};

const buildCols = (list) => {
  const cols = BASE.map(([k, l]) => ({ l, f: (e) => val(e, k) }));
  DOC_TYPES.forEach((d) => {
    cols.push({ l: `انتهاء ${d.label}`, f: (e) => e.documents?.[d.key]?.expiry_date || "" });
    if (d.key === "operation_card") cols.push({ l: "رقم كرت التشغيل", f: (e) => e.documents?.[d.key]?.card_number || "" });
  });
  const extra = [...new Set(list.flatMap((e) => (e.custom_documents || []).map((c) => c.label).filter(Boolean)))];
  extra.forEach((lb) => cols.push({ l: `انتهاء ${lb}`, f: (e) => (e.custom_documents || []).find((c) => c.label === lb)?.expiry_date || "" }));
  return cols;
};

const exportExcel = async (list) => {
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("الموظفين", { views: [{ rightToLeft: true, state: "frozen", ySplit: 1 }] });
  const cols = buildCols(list);
  ws.columns = cols.map((c) => ({ header: c.l, width: Math.max(14, c.l.length + 4) }));
  list.forEach((e) => ws.addRow(cols.map((c) => c.f(e))));
  const h = ws.getRow(1);
  h.font = { bold: true, color: { argb: "FFFFFFFF" } };
  h.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1E40AF" } };
  h.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  h.height = 30;
  ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: cols.length } };
  const buf = await wb.xlsx.writeBuffer();
  await saveBlob(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `بيانات_الموظفين_${list.length}.xlsx`);
};

const esc = (s) => String(s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

const cardHtml = (e, cols) => `
  <div style="border:1px solid #cbd5e1;border-radius:10px;padding:14px;margin-bottom:14px;background:#fff">
    <div style="font-size:17px;font-weight:700;color:#1e40af;border-bottom:2px solid #1e40af;padding-bottom:6px;margin-bottom:8px">${esc(e.name || "")} <span style="font-size:12px;color:#64748b">${esc(e.employee_number || "")}</span></div>
    <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:4px 14px;font-size:11.5px">
      ${cols.slice(1).map((c) => { const v = c.f(e); return v === "" ? "" : `<div><span style="color:#64748b">${esc(c.l)}:</span> <b><bdi>${esc(v)}</bdi></b></div>`; }).join("")}
    </div>
  </div>`;

const exportPdf = async (list, onProgress) => {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import("html2canvas"), import("jspdf")]);
  const cols = buildCols(list);
  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  const host = document.createElement("div");
  host.style.cssText = "position:fixed;left:-10000px;top:0;width:794px;padding:24px;background:#f8fafc;direction:rtl;font-family:'IBM Plex Sans Arabic',Tahoma,sans-serif;color:#0f172a";
  document.body.appendChild(host);
  try {
    const per = 3;
    for (let i = 0; i < list.length; i += per) {
      host.innerHTML = list.slice(i, i + per).map((e) => cardHtml(e, cols)).join("");
      const cv = await html2canvas(host, { scale: 1.5, backgroundColor: "#f8fafc" });
      const w = 210, hgt = Math.min(297, (cv.height * w) / cv.width);
      if (i) pdf.addPage();
      pdf.addImage(cv.toDataURL("image/jpeg", 0.8), "JPEG", 0, 0, w, hgt);
      onProgress(Math.min(i + per, list.length));
    }
  } finally { host.remove(); }
  await saveBlob(pdf.output("blob"), `بيانات_الموظفين_${list.length}.pdf`, "application/pdf");
};

export const EmployeesExport = ({ employees, selected }) => {
  const [busy, setBusy] = useState("");
  const list = selected?.size ? employees.filter((e) => selected.has(e.id)) : employees;
  const run = async (kind) => {
    if (!list.length) { toast.warning("لا يوجد موظفين للتصدير"); return; }
    setBusy(kind);
    try {
      if (kind === "xlsx") await exportExcel(list);
      else await exportPdf(list, (n) => setBusy(`pdf ${n}/${list.length}`));
      toast.success(`تم تصدير ${list.length} موظف`);
    } catch { toast.error("تعذّر التصدير"); } finally { setBusy(""); }
  };
  return (
    <DropdownMenu dir="rtl">
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs text-indigo-700 border-indigo-200" disabled={!!busy} data-testid="employees-export-btn">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
          {busy.startsWith("pdf ") ? busy.slice(4) : `تصدير بيانات الموظفين (${list.length})`}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem data-testid="employees-export-xlsx" onClick={() => run("xlsx")}><FileSpreadsheet className="h-4 w-4 ml-2 text-emerald-600" /> Excel</DropdownMenuItem>
        <DropdownMenuItem data-testid="employees-export-pdf" onClick={() => run("pdf")}><FileText className="h-4 w-4 ml-2 text-red-600" /> PDF</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
